import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Sticky, persistent banner shown across the whole app whenever the current
 * session is inside an active admin impersonation session.
 *
 * An "active" session = a row in admin_impersonation_log where
 * target_user_id = auth.uid() AND ended_at IS NULL.
 *
 * RLS on admin_impersonation_log already allows the target to see their own
 * rows, so this query is safe for every signed-in user.
 */
const ImpersonationBanner = () => {
  const { user } = useAuth();
  const [active, setActive] = useState<{ started_at: string; reason: string | null; ticket_ref: string | null } | null>(null);

  useEffect(() => {
    if (!user) {
      setActive(null);
      return;
    }
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from("admin_impersonation_log")
        .select("started_at, reason, ticket_ref")
        .eq("target_user_id", user.id)
        .is("ended_at", null)
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) setActive(data ?? null);
    };

    load();
    const iv = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(iv);
    };
  }, [user]);

  if (!active) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-[100] w-full bg-red-600 text-white shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 text-sm">
        <ShieldAlert className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span className="font-semibold">Admin support session active</span>
        <span className="opacity-90 hidden sm:inline">
          A GetPetVet admin is signed in as this account for support purposes. All actions are audited.
        </span>
        {active.ticket_ref && (
          <span className="ml-auto text-xs bg-white/15 rounded px-2 py-0.5 font-mono">
            {active.ticket_ref}
          </span>
        )}
      </div>
    </div>
  );
};

export default ImpersonationBanner;
